import { useTranslation } from 'react-i18next';
import { Grid, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import moment from 'moment';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { useLanguage } from '../../common/GeneralUtils';
import { getDateFormat, getTimeFormat } from '../../common/getDateAndTimeFormat';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { OrderPageIntent } from '../OrderPageIntent';
import TimeUtils from '../../common/TimeUtils';
import { UIObjectTypes } from '../../api';
import { MULTIPLE_TICKETS_DETAILS_FIELD_NAME } from '../OrderUtils';

export const OrderFinalDetailsTable = ({
  orderPageIntent,
  shouldShowDuration,
}: {
  shouldShowDuration: boolean;
  orderPageIntent: OrderPageIntent;
}) => {
  const { t } = useTranslation();
  const { orderPageState } = useOrderPage();
  const language = useLanguage();
  const { account } = orderPageState;
  const timeFormat = getTimeFormat(account);
  const objectWorkShopType = orderPageState.orderPage.objects.find((x: any) => x.name === 'workshopType');
  const selectedWorkshopType = orderPageState.details.custom_details?.workshopType;
  const objectMultipleTickets = orderPageState.orderPage.objects.find(
    (x: any) => x.type === UIObjectTypes.MULTIPLE_TICKETS,
  );
  const multipleTicketsDetails = orderPageState.details.custom_details?.[MULTIPLE_TICKETS_DETAILS_FIELD_NAME];

  const renderTableRow = (label: string, value: React.ReactNode) => (
    <TableRow
      sx={{
        '& td': {
          border: 'none',
          padding: '0px 16px',
        },
      }}>
      <TableCell width="80px">
        <OrderPageTypography variant={'h6'} bold>
          {label}
        </OrderPageTypography>
      </TableCell>
      <TableCell>
        <OrderPageTypography variant={'h6'}>{value}</OrderPageTypography>
      </TableCell>
    </TableRow>
  );

  return (
    <Grid container flexDirection="column" gap={0.5} mt={1}>
      <OrderPageTypography variant="h3" bold>
        {t('orderdetails')}
      </OrderPageTypography>
      <TableContainer>
        <Table size="small">
          <TableBody>
            {orderPageIntent === OrderPageIntent.BUY_NOW && (
              <>
                {renderTableRow(
                  t('order_page.date'),
                  moment(orderPageState.dateAndTimeDetails.date).format(getDateFormat(account).longDateFormat),
                )}
                {renderTableRow(
                  t('order_page.time'),
                  moment.unix(orderPageState.dateAndTimeDetails.time).tz('utc').format(timeFormat),
                )}
              </>
            )}
            {renderTableRow(t('order_page.persons'), orderPageState.details.persons)}
            {shouldShowDuration &&
              renderTableRow(
                t('order_page.duration', {
                  activityLabelSingular: orderPageState.account?.activity_label_singular?.[language] || '',
                }),
                TimeUtils.formatDuration(orderPageState.details.duration, t),
              )}
            {objectWorkShopType &&
              selectedWorkshopType &&
              renderTableRow(
                t('order_page.workshop'),
                objectWorkShopType.options.find((x: any) => x.value === selectedWorkshopType)?.label[language],
              )}
            {objectMultipleTickets && multipleTicketsDetails && (
              <TableRow>
                <TableCell>
                  <OrderPageTypography bold>{t('order_page.workshopChoice')}</OrderPageTypography>
                </TableCell>
                <TableCell>
                  <Grid container flexDirection="column" gap={1}>
                    {Object.entries(multipleTicketsDetails).map(([key, value]) => (
                      <Grid container key={key} gap={1}>
                        <OrderPageTypography>
                          {objectMultipleTickets.tickets.find((x: any) => x.id === key)?.label[language]}:
                        </OrderPageTypography>
                        <OrderPageTypography>{value as number}</OrderPageTypography>
                      </Grid>
                    ))}
                  </Grid>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};
